import { FC } from "react";
import { useForm, SubmitHandler, Controller } from "react-hook-form";

import { ContactCard } from "../../cards/ContactCard";
import { Button, Icon, Input, Label, Modal } from "../../common";
import { FormErrorMessage } from "../../messages";
import { useCreateContactMutation } from "../../../store/services/contacts";
import { BaseContactLink, ContactStatus } from "../../../types/ContactTypes";

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export const NewContactModal: FC<Props> = ({ isOpen, onClose }) => {
  const {
    handleSubmit,
    control,
    formState: { isValid },
  } = useForm<BaseContactLink>({
    defaultValues: {
      first_name: "",
      last_name: "",
      email: "",
    },
  });

  const [
    createContact,
    { data: newContact, isSuccess, isLoading, isError, error },
  ] = useCreateContactMutation();

  const onSubmit: SubmitHandler<BaseContactLink> = async (data) => {
    createContact(data);
  };

  return isSuccess ? (
    <Modal
      title="Invited!"
      subtitle="To send or receive payments from the contacts you should wait until they are registered and verified"
      isOpen={isOpen}
      close={onClose}
    >
      <div className="flex flex-col gap-10 items-center">
        {newContact && (
          <ContactCard
            title={newContact.email}
            text={newContact.first_name + " " + newContact.last_name}
            status={ContactStatus.Pending}
          />
        )}

        <Button variant={Button.variant.TEMPTING} onClick={() => onClose()}>
          Great, thank you!
        </Button>
      </div>
    </Modal>
  ) : (
    <Modal
      title="New contact"
      subtitle="New contacts will have to register and get verified on Archway"
      isOpen={isOpen}
      close={onClose}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-12">
        <div className="flex flex-col gap-8">
          <div className="flex flex-col md:flex-row gap-8">
            <Label label="First name">
              <Controller
                name="first_name"
                control={control}
                rules={{ required: true }}
                render={({ field }) => <Input {...field} type="text" />}
              />
            </Label>

            <Label label="Last name">
              <Controller
                name="last_name"
                control={control}
                rules={{ required: true }}
                render={({ field }) => <Input {...field} type="text" />}
              />
            </Label>
          </div>

          <Label label="Email">
            <Controller
              name="email"
              control={control}
              rules={{ required: true }}
              render={({ field }) => <Input {...field} type="text" />}
            />
          </Label>
        </div>

        <FormErrorMessage isError={isError} error={error} />

        <div className="grow md:hidden"></div>

        <div className="mt-auto flex flex-col items-center justify-between gap-4">
          <Button
            type="submit"
            variant={Button.variant.PRIMARY}
            className="w-full"
            contentClassName="w-full flex flex-row justify-between"
            disabled={!isValid || isLoading}
          >
            Invite
            <Icon icon="Send" color="white" size={24} />
          </Button>

          <Button
            variant={Button.variant.TERTIARY}
            className="w-full"
            onClick={() => onClose()}
            disabled={isLoading}
          >
            Go Back
          </Button>
        </div>
      </form>
    </Modal>
  );
};
